import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseQuery';
import {
  MilestoneFeedbackData,
  FullBudgetFeedbackFormResponse,
  StoreOrderFeedbacksResponse,
  BiddingFeedbacksListResponse,
} from '@/types/feedbackService';
import {
  GetShipmentFeedbacksApiParams,
  GetShipmentFeedbacksApiResponse,
  ShipmentFeedbackDetailResponse,
} from '@/types/logisticsFeedback';

export const feedbackService = createApi({
  reducerPath: 'feedbackService',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['FEEDBACK_LIST', 'FEEDBACK_ITEM'],

  endpoints: ({ query }) => ({
    // 1. Milestone Feedback
    getBuyerMilestoneFeedback: query<
      { data: MilestoneFeedbackData } | { payload: MilestoneFeedbackData },
      string
    >({
      query: (postId) => ({
        url: `/feedback-service/v1/bidding-requests/${postId}/buyer-feedback/items`,
        method: 'GET',
      }),
      providesTags: (_result, _error, postId) => [{ type: 'FEEDBACK_ITEM', id: `MILESTONE_BUYER_${postId}` }],
    }),

    getSellerMilestoneFeedback: query<
      { data: MilestoneFeedbackData } | { payload: MilestoneFeedbackData },
      string
    >({
      query: (postId) => ({
        url: `/feedback-service/v1/bidding-requests/${postId}/seller-feedback/items`,
        method: 'GET',
      }),
      providesTags: (_result, _error, postId) => [{ type: 'FEEDBACK_ITEM', id: `MILESTONE_SELLER_${postId}` }],
    }),

    // 2. Full Budget Feedback
    getBuyerFullBudgetFeedback: query<
      { data: FullBudgetFeedbackFormResponse } | { payload: FullBudgetFeedbackFormResponse },
      string
    >({
      query: (postId) => ({
        url: `/feedback-service/v1/bidding-requests/${postId}/buyer-feedback`,
        method: 'GET',
      }),
      providesTags: (_result, _error, postId) => [{ type: 'FEEDBACK_ITEM', id: `FULLBUDGET_BUYER_${postId}` }],
    }),

    getSellerFullBudgetFeedback: query<
      { data: FullBudgetFeedbackFormResponse } | { payload: FullBudgetFeedbackFormResponse },
      string
    >({
      query: (postId) => ({
        url: `/feedback-service/v1/bidding-requests/${postId}/seller-feedback`,
        method: 'GET',
      }),
      providesTags: (_result, _error, postId) => [{ type: 'FEEDBACK_ITEM', id: `FULLBUDGET_SELLER_${postId}` }],
    }),

    getPostBiddingFeedbacks: query<
      { data: StoreOrderFeedbacksResponse } | { payload: StoreOrderFeedbacksResponse },
      string
    >({
      query: (postId) => ({
        url: `/feedback-service/v1/bidding-feedbacks/post/${postId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, postId) => [{ type: 'FEEDBACK_ITEM', id: `POST_FEEDBACKS_${postId}` }],
    }),

    // 3. Store Order & Global Feedbacks
    getStoreOrderFeedbacks: query<
      { data: StoreOrderFeedbacksResponse } | { payload: StoreOrderFeedbacksResponse },
      string
    >({
      query: (orderId) => ({
        url: `/aggregation-service/v1/orders/${orderId}/feedbacks`,
        method: 'GET',
      }),
      providesTags: (_result, _error, orderId) => [{ type: 'FEEDBACK_ITEM', id: `ORDER_FEEDBACKS_${orderId}` }],
    }),

    getAllBiddingFeedbacks: query<
      BiddingFeedbacksListResponse,
      { page?: number; limit?: number } | void
    >({
      query: (params) => ({
        url: `/feedback-service/v1/bidding-feedbacks/all`,
        method: 'GET',
        params: params || undefined,
      }),
      providesTags: [{ type: 'FEEDBACK_LIST', id: 'LIST' }],
    }),

    // 4. Operator Shipment Feedbacks
    getShipmentFeedbacks: query<
      GetShipmentFeedbacksApiResponse,
      GetShipmentFeedbacksApiParams | void
    >({
      query: (params) => ({
        url: '/aggregation-service/v1/operator/shipment-feedbacks',
        method: 'GET',
        params: params || undefined,
      }),
      providesTags: (result) => {
        const list = result?.payload?.content ?? result?.content;
        return list
          ? [
              ...list.map((item) => ({
                type: 'FEEDBACK_ITEM' as const,
                id: item.id || item.orderSerial,
              })),
              { type: 'FEEDBACK_LIST', id: 'LIST' },
            ]
          : [{ type: 'FEEDBACK_LIST', id: 'LIST' }];
      },
    }),

    // 5. Operator Shipment Feedback Details
    getShipmentFeedbackMilestones: query<
      ShipmentFeedbackDetailResponse,
      string
    >({
      query: (id) => ({
        url: `/aggregation-service/v1/operator/shipment-feedbacks/${id}/milestones`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [
        { type: 'FEEDBACK_ITEM', id: `MILESTONES_${id}` },
      ],
    }),

    getShipmentFeedbackMarketplace: query<
      ShipmentFeedbackDetailResponse,
      string
    >({
      query: (id) => ({
        url: `/aggregation-service/v1/operator/shipment-feedbacks/${id}/marketplace`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [
        { type: 'FEEDBACK_ITEM', id: `MARKETPLACE_${id}` },
      ],
    }),

    getShipmentFeedbackFullBudget: query<
      ShipmentFeedbackDetailResponse,
      string
    >({
      query: (id) => ({
        url: `/aggregation-service/v1/operator/shipment-feedbacks/${id}/full-budget`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [
        { type: 'FEEDBACK_ITEM', id: `FULL_BUDGET_${id}` },
      ],
    }),
  }),
});

export const {
  useGetBuyerMilestoneFeedbackQuery,
  useLazyGetBuyerMilestoneFeedbackQuery,
  useGetSellerMilestoneFeedbackQuery,
  useLazyGetSellerMilestoneFeedbackQuery,
  useGetBuyerFullBudgetFeedbackQuery,
  useLazyGetBuyerFullBudgetFeedbackQuery,
  useGetSellerFullBudgetFeedbackQuery,
  useLazyGetSellerFullBudgetFeedbackQuery,
  useGetPostBiddingFeedbacksQuery,
  useLazyGetPostBiddingFeedbacksQuery,
  useGetStoreOrderFeedbacksQuery,
  useLazyGetStoreOrderFeedbacksQuery,
  useGetAllBiddingFeedbacksQuery,
  useLazyGetAllBiddingFeedbacksQuery,
  useGetShipmentFeedbacksQuery,
  useLazyGetShipmentFeedbacksQuery,
  useGetShipmentFeedbackMilestonesQuery,
  useLazyGetShipmentFeedbackMilestonesQuery,
  useGetShipmentFeedbackMarketplaceQuery,
  useLazyGetShipmentFeedbackMarketplaceQuery,
  useGetShipmentFeedbackFullBudgetQuery,
  useLazyGetShipmentFeedbackFullBudgetQuery,
} = feedbackService;
